export type SectionId =
  | "home"
  | "about"
  | "education"
  | "research"
  | "teaching"
  | "awards"
  | "activities"
  | "skills"
  | "contact";

export type NavItem = { id: SectionId; label: string };

export type Profile = {
  nameZh: string;
  nameEn: string;
  city: string;
  email: string;
  phoneFull: string;
  phoneMasked: string;
  avatarUrl: string;
  tagline: string;
  intro: string;
};

export type EducationItem = {
  stage: "硕士" | "本科";
  school: string;
  college?: string;
  major: string;
  degreeType?: string;
  time: string;
  gpa: string;
  rankHint: string;
  courses: string[];
  courseHighlights: { course: string; score: string }[];
};

export type PublicationItem = {
  title: string;
  venue: string;
  role: string;
  levelHint?: string;
  date: string;
  doi?: string;
  status?: string;
};

export type TimelineItem = { time: string; detail: string };

export type AwardItem = { title: string; year?: string; note?: string };

export type TagGroup = { title: string; items: string[] };

export type SocialLink = { label: string; href: string; note?: string };

export const site = {
  nav: [
    { id: "home", label: "首页" },
    { id: "about", label: "关于" },
    { id: "education", label: "教育" },
    { id: "research", label: "科研" },
    { id: "teaching", label: "教学" },
    { id: "awards", label: "荣誉" },
    { id: "activities", label: "活动" },
    { id: "skills", label: "兴趣" },
    { id: "contact", label: "联系" }
  ] satisfies NavItem[],

  profile: {
    nameZh: "梁英轩",
    nameEn: "Yingxuan Liang",
    city: "广州",
    email: "2268575662@qq.com",
    phoneFull: "13527863649",
    phoneMasked: "135****3649",
    avatarUrl: "/avatar.jpg",
    tagline: "数学与统计背景 · 智能优化 / 多目标优化 / 联邦学习",
    intro:
      "我长期在数学、优化与模式识别相关方向学习与探索。对科研保持热情，也重视表达与教学：希望把抽象方法讲清楚、把实验设计做扎实、把结论写得可复现。"
  } satisfies Profile,

  about: {
    paragraphs: [
      "我目前在广东工业大学数学与统计学院攻读数学专业学术型硕士（2023/09–2026/06），本科为应用统计学（2019/09–2023/06）。",
      "研究层面，我关注智能优化、多目标优化与演化计算，也持续探索联邦学习、深度学习、模式识别与机器学习相关方法。",
      "实践层面，我有持续的数学教学经历（高中/初中/考研数学），重视耐心、清晰与可迁移的讲解方式。生活里喜欢运动、吉他、小提琴，整体风格偏真实、稳定、理性。"
    ]
  },

  researchInterests: [
    "智能优化",
    "多目标优化",
    "联邦学习",
    "深度学习",
    "演化计算",
    "模式识别",
    "数学建模",
    "机器学习相关方法探索"
  ],

  education: [
    {
      stage: "硕士",
      school: "广东工业大学",
      college: "数学与统计学院",
      major: "数学",
      degreeType: "学术型硕士",
      time: "2023/09 – 2026/06",
      gpa: "94.2/100",
      rankHint: "专业前10%",
      courses: [
        "泛函分析",
        "数值分析",
        "随机过程",
        "抽象代数",
        "群智能算法及应用",
        "模式识别",
        "英文科技论文写作与学术报告"
      ],
      courseHighlights: [
        { course: "数值分析", score: "98" },
        { course: "模式识别", score: "100" },
        { course: "泛函分析", score: "95" },
        { course: "随机过程", score: "94" },
        { course: "英文科技论文写作与学术报告", score: "100" }
      ]
    },
    {
      stage: "本科",
      school: "广东工业大学",
      college: "数学与统计学院",
      major: "应用统计学",
      degreeType: "理学学士",
      time: "2019/09 – 2023/06",
      gpa: "91.7/100",
      rankHint: "专业前10%",
      courses: [
        "数学分析",
        "高等代数",
        "多元统计分析",
        "时间序列分析",
        "最优化方法",
        "模式识别",
        "概率论",
        "数理统计",
        "非参数统计",
        "数据挖掘"
      ],
      courseHighlights: []
    }
  ] satisfies EducationItem[],

  publications: [
    {
      title: "Investigating the Spreadability of Problems in Evolutionary Multi-objective Optimization Algorithms",
      venue: "GECCO '25 Companion",
      role: "学生二作",
      levelHint: "CCF C",
      date: "2025/08/11",
      doi: "10.1145/3712255.3726605"
    },
    {
      title: "Population Diversity Dynamics Analysis for Imbalanced Multi-objective Optimization",
      venue: "IEEE Transactions on Evolutionary Computation",
      role: "学生一作",
      levelHint: "中科院一区",
      date: "2026/03/09",
      doi: "10.1109/TEVC.2026.3672142"
    },
    {
      title: "Pareto Set Learning in Multi-objective Multitask Optimization",
      venue: "WCCI 2026",
      role: "学生二作",
      levelHint: "CCF C",
      date: "2026/02/01",
      status: "投稿"
    }
  ] satisfies PublicationItem[],

  teaching: [
    {
      time: "2019 – 2023",
      detail: "教授高中数学，帮助学生从约60分提升到高考数学117分，并考入南方医科大学"
    },
    {
      time: "2022 – 2023",
      detail: "教授初三数学，帮助学生从约70分提升到中考数学约90分，并考入本地知名公立学校"
    },
    {
      time: "2022 – 2023",
      detail: "教授高二、高三数学，帮助学生数学高考取得122分，并考入广东工业大学"
    },
    { time: "2023 – 2024", detail: "暑期带高二数学小班" },
    {
      time: "2023 – 至今",
      detail: "带考研数学，指导两名学生考入广东工业大学研究生院"
    },
    {
      time: "2024 – 2025",
      detail: "在鲸飞教育科技有限公司、硕博联盟教育咨询有限公司担任数学分析与高等代数专业课指导老师"
    }
  ] satisfies TimelineItem[],

  awards: [
    { title: "全国大学生数学竞赛二等奖", year: "2019" },
    { title: "全国大学生数学竞赛三等奖", year: "2020" },
    { title: "“华为杯”中国研究生数学建模竞赛二等奖", year: "2023" },
    { title: "“华为杯”中国研究生数学建模竞赛二等奖", year: "2024" },
    { title: "硕士生学业一等奖学金" },
    { title: "硕士生学业二等奖学金" },
    { title: "研究生学术论坛优胜奖" }
  ] satisfies AwardItem[],

  activities: [
    { time: "校园 / 学术", detail: "受邀参加两院竞赛科研分享会并向新生做经验分享" },
    { time: "校园 / 学术", detail: "参加首届研究生学术论坛并获优胜奖" },
    { time: "组织经历", detail: "曾担任班长、学科助理" }
  ] satisfies TimelineItem[],

  skillsAndInterests: [
    {
      title: "研究与方法",
      items: ["演化计算", "多目标优化", "联邦学习", "深度学习", "模式识别", "数学建模"]
    },
    {
      title: "学习与表达",
      items: ["持续学习", "清晰表达", "耐心讲解", "结构化写作", "复盘与迭代"]
    },
    { title: "个人兴趣", items: ["运动", "吉他", "小提琴"] }
  ] satisfies TagGroup[],

  contact: {
    notes: [
      "这是公开个人主页：不展示家庭详细住址。",
      "手机号默认仅展示部分；如需完整号码请通过邮件联系。"
    ],
    social: [
      { label: "Email", href: "mailto:2268575662@qq.com", note: "邮件联系（推荐）" }
    ] satisfies SocialLink[]
  }
} as const;

